import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import NucleusPage from '../../components/nucleus/NucleusPage';
import Inspector, { Band, Facts, Note, useOpen } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import DataTable from '../../components/nucleus/DataTable';
import { TitledSet } from '../../components/nucleus/SetHead';
import { useToast } from '../../components/common';
import { useAuth } from '../../contexts/AuthContext';
import { usePlace } from '../../nucleus/place';
import api from '../../services/api';
import { FormColumn, FormBand, ActVerb } from './selfForm';
import Segmented from '../../components/nucleus/Segmented';
import { RecoveryBand } from './RecoveryCodes';
import {
    SESSION_PRESETS, formatDuration, sessionName, deviceOf,
} from './selfData';
import { ago, dateOf, whenOf, plural } from '../../nucleus/format';

// SECURITY — how you sign in and stay signed in, as two tabs: where you are
// signed in (every session, each openable, and the last 30 days of sign-ins),
// and the sign-in settings (how long a session lasts, your recovery codes). The
// tab rides the address (?tab=settings). Personal only.
const TABS = [{ value: 'signed-in', label: 'Signed in' }, { value: 'settings', label: 'Sign-in settings' }];

// How a session was opened, in words (the server's auth_method).
const METHODS = {
    password: 'password', recovery_code: 'recovery code', handoff: 'from a web sign-in',
    app_token: 'Nucleus for Mac', app_consent: 'app consent', test_user: 'test user',
    cosm_delegated: 'made by COSM for an app you allowed',
};
// Why a sign-in failed, in words (the server's reason).
const FAILURES = {
    invalid_password: 'wrong password', invalid_recovery_code: 'wrong recovery code',
    recovery_code_used: 'recovery code already used', account_disabled: 'account disabled',
    not_approved: 'account not yet approved',
};
// when a time was, as a number to sort by (nothing sorts last)
const when = (iso) => (iso ? new Date(iso).getTime() : null);
// sign-ins: what happened, from what, where, how and when (the newest first)
const SIGN_IN_COLUMNS = [
    { id: 'result', label: 'Result', lock: true, min: 160, grow: 1.6, value: (a) => (a.success ? 'Signed in' : 'Failed'),
      cell: (a) => (a.success ? 'Signed in' : `Failed: ${FAILURES[a.reason] || 'refused'}`) },
    { id: 'device', label: 'Device', min: 130, grow: 1.4, value: (a) => a.device || null, cell: (a) => a.device || '—' },
    { id: 'place', label: 'Place', min: 110, grow: 1, value: (a) => a.place || null, cell: (a) => a.place || '—' },
    { id: 'method', label: 'Method', min: 100, grow: 0.8, value: (a) => METHODS[a.method] || null, cell: (a) => METHODS[a.method] || '—' },
    { id: 'when', label: 'When', min: 120, grow: 0, value: (a) => when(a.at), cell: (a) => whenOf(a.at) },
];
export default function SecurityPage() {
    const { section, page, to, navigate } = usePlace();
    const { logout } = useAuth();
    const toast = useToast();
    const [me, setMe] = useState(null);
    const [sessions, setSessions] = useState(null);
    const [signIns, setSignIns] = useState(null);
    const [apps, setApps] = useState({});
    const [openId, open, close] = useOpen();
    const [params, setParams] = useSearchParams();
    const tab = params.get('tab') === 'settings' ? 'settings' : 'signed-in';
    const setTab = (v) => setParams((p) => { const q = new URLSearchParams(p); if (v === 'settings') q.set('tab', v); else q.delete('tab'); q.delete('open'); return q; }, { replace: true });

    const loadSessions = useCallback(() => api.request('/auth/sessions')
        .then((s) => setSessions(s || { sessions: [] }))
        .catch(() => setSessions({ sessions: [], error: true })), []);
    useEffect(() => {
        api.request('/auth/me').then(setMe).catch(() => setMe({}));
        loadSessions();
        api.request('/auth/sign-ins')
            .then((r) => setSignIns(r?.sign_ins || []))
            .catch(() => setSignIns({ error: true }));
        api.request('/app-shell/apps')
            .then((r) => setApps(Object.fromEntries((r?.apps || []).map((row) => row.app || row).map((a) => [String(a.id), a.name]))))
            .catch(() => {});
    }, [loadSessions]);

    // the app's name: the session's own (the server sends it now), else the hub's list
    const appName = (id) => apps[String(id)];
    // where it is: the name the client declared (device_label), else what its User-Agent reads as
    const placeOf = (s) => s.device_label || deviceOf(s.user_agent);
    const methodOf = (s) => METHODS[s.auth_method] || null;
    // A browser session is named for its device; an app's, the Mac hub's or dev.oS's
    // for what it is, with its device beside the place.
    const isSurface = (s) => !!s.app_id || s.audience === 'nucleus-mac' || s.audience === 'builder' || !!s.delegated;
    const nameOf = (s) => (isSurface(s) ? sessionName(s, appName) : placeOf(s) || sessionName(s, appName));
    const all = sessions?.sessions || [];
    // THE MAC'S APPS SIT UNDER THE MAC. A token the hub minted is a session of
    // its own (parent_session_id), and ending the hub's ends it; listing it
    // flat beside the hub made one Mac look like several places.
    const ids = new Set(all.map((s) => String(s.id)));
    const isChild = (s) => s.parent_session_id && ids.has(String(s.parent_session_id));
    const kidsOf = (s) => all.filter((k) => String(k.parent_session_id) === String(s.id));
    const rows = all.filter((s) => !isChild(s));
    const hereId = sessions?.current_session_id;
    const here = rows.filter((s) => s.id === hereId);
    const there = rows.filter((s) => s.id !== hereId);
    const current = all.find((s) => String(s.id) === String(openId)) || null;

    const signOutHere = {
        id: 'here', label: 'Sign out here',
        confirm: { title: 'Sign out of this browser?', body: 'This browser’s session ends. Everywhere else stays signed in.', label: 'Sign out' },
        run: () => logout?.({ scope: 'app' }),
    };
    const signOutEverywhere = {
        id: 'everywhere', label: 'Sign out everywhere', danger: true,
        confirm: { title: 'Sign out everywhere?', body: `Every session ends${rows.length ? ` (${plural(rows.length, 'place')})` : ''}, this browser too. Your apps stay approved and your data is untouched.`, label: 'Sign out everywhere' },
        run: () => logout?.({ scope: 'global' }),
    };
    // Everything but this browser (POST /auth/logout scope=others): the
    // lost-phone control that does not also sign you out of where you are.
    const signOutElsewhere = {
        id: 'elsewhere', label: 'Sign out everywhere else', danger: true,
        disabled: !there.length, hint: there.length ? undefined : 'This browser is the only place you’re signed in',
        confirm: { title: 'Sign out everywhere else?', body: `Every other session ends (${plural(there.length, 'place')}). This browser stays signed in. Your apps stay approved and your data is untouched.`, label: 'Sign out everywhere else' },
        run: async () => {
            try {
                await api.logout('others');
                toast.success('Signed out everywhere else');
                close();
                loadSessions();
            } catch (e) { toast.error(e?.message || 'Could not sign out everywhere else'); }
        },
    };
    // END ONE SESSION, anywhere (from main: POST /auth/sessions/<id>/revoke —
    // it takes what was minted under it too, e.g. a Mac hub's app tokens). This
    // browser's own session is a sign-out instead: revoking it as someone
    // else's would leave the page holding a token the server no longer honours.
    const endOne = (s) => ({
        id: 'end', label: 'End this session', danger: true,
        confirm: { title: `End ${sessionName(s, appName)}?`, body: `${placeOf(s) || 'That session'} is signed out at once. Your apps stay approved and your data is untouched.`, label: 'End session' },
        run: async () => {
            try {
                await api.revokeSession(s.id);
                toast.success('Session ended');
                if (String(openId) === String(s.id)) close();
                loadSessions();
            } catch (e) { toast.error(e?.message || 'Could not end that session'); }
        },
    });
    const actionsOf = (s) => (s.id === hereId ? [signOutHere, signOutElsewhere, signOutEverywhere] : [endOne(s), signOutEverywhere]);

    // a session: its device (an app's or the Mac hub's, what it is, with its
    // device beside it), where, how and when it signed in, the apps under it,
    // and when it was last seen ("Here" for this browser)
    const sessionColumns = [
        { id: 'device', label: 'Device', lock: true, min: 180, grow: 2, value: nameOf,
          cell: (s) => [nameOf(s), isSurface(s) && placeOf(s)].filter(Boolean).join(' · ') },
        { id: 'where', label: 'Where', min: 110, grow: 1, value: (s) => s.location || null, cell: (s) => s.location || '—' },
        { id: 'method', label: 'Method', min: 110, grow: 0.8, keep: 2, value: (s) => methodOf(s), cell: (s) => methodOf(s) || '—' },
        { id: 'signed-in', label: 'Signed in', min: 100, grow: 0, keep: 1, value: (s) => when(s.created_at), cell: (s) => ago(s.created_at) },
        { id: 'apps', label: 'Apps', num: true, min: 64, grow: 0, keep: 3, value: (s) => kidsOf(s).length, cell: (s) => (kidsOf(s).length ? plural(kidsOf(s).length, 'app') : '—') },
        { id: 'seen', label: 'Last seen', min: 100, grow: 0, value: (s) => when(s.last_seen_at || s.created_at),
          cell: (s) => (s.id === hereId ? 'Here' : ago(s.last_seen_at || s.created_at)) },
    ];
    const sessionTable = (id, label, list) => (
        <DataTable
            id={id} label={label} columns={sessionColumns} rows={list} keyOf={(s) => s.id} fit
            sort={{ id: 'seen', dir: 'desc' }} currentKey={openId} onOpen={open} actionsOf={actionsOf}
        />
    );

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            verbs={<>
                <ActVerb act={signOutHere} />
                <ActVerb act={signOutElsewhere} />
                <ActVerb act={signOutEverywhere} />
            </>}
            inspector={current && (
                <Inspector
                    key={current.id}
                    tag={current.id === hereId ? 'Session · This browser' : `Session${current.app_id ? ' · App' : ''}`}
                    title={nameOf(current)}
                    sub={[sessionName(current, appName), current.location].filter(Boolean).join(' · ')}
                    actions={actionsOf(current)}
                    onClose={close}
                >
                    <Band title="This session">
                        <Facts items={[
                            { label: 'Signed in', value: dateOf(current.created_at) },
                            { label: 'Last seen', value: ago(current.last_seen_at || current.created_at) },
                            { label: 'Ends', value: current.expires_at ? dateOf(current.expires_at) : 'When it idles out' },
                            (current.ip || current.location) && { label: 'From', value: [current.ip && String(current.ip), current.location].filter(Boolean).join(' · ') },
                            methodOf(current) && { label: 'Signed in with', value: methodOf(current).replace(/^./, (c) => c.toUpperCase()) },
                            placeOf(current) && { label: 'Device', value: placeOf(current) },
                        ]} />
                    </Band>
                    {kidsOf(current).length > 0 && (
                        <Band title="Apps inside">
                            <Note>Each app on this device signs in under it. Ending this session ends them too.</Note>
                            {kidsOf(current).map((k) => (
                                <Row key={k.id} name={sessionName(k, appName)} sub={`seen ${ago(k.last_seen_at || k.created_at)}`} actions={[endOne(k)]} />
                            ))}
                        </Band>
                    )}
                    {current.app_id && (
                        <Band title="Its app">
                            <Note>Ending its session signs it out; the app stays approved, and what it reaches is set on its grant.</Note>
                            <Row name={appName(current.app_id) || 'The app'} sub="Open it in Apps" onPress={() => navigate(to('apps', null, `open=${current.app_id}`))} />
                        </Band>
                    )}
                    {current.user_agent && (
                        <Band title="About">
                            <Note>{current.user_agent}</Note>
                        </Band>
                    )}
                </Inspector>
            )}
        >
            <div className="self-tabs">
                <Segmented label="Security" options={TABS} value={tab} onChange={setTab} />
            </div>
            {tab === 'settings' ? (
                <FormColumn>
                    {me && <SessionBand initial={me.session_duration ?? null} />}
                    <RecoveryBand />
                </FormColumn>
            ) : (<>
            <FormColumn>
                {sessions && (
                    <FormBand title="Signed in">
                        <Facts items={[
                            { label: 'Places', value: String(rows.length) },
                            { label: 'Apps signed in', value: String(all.filter((s) => s.app_id).length) },
                        ]} />
                        <Note>One sign-in, never one grant: each app keeps its own approval. Signing out ends sessions; apps stay approved and your data is untouched.</Note>
                        {sessions.error && <Note tone="warn">Couldn’t read where you’re signed in.</Note>}
                    </FormBand>
                )}
            </FormColumn>
            {sessions && !sessions.error && (
                <div className="self-rows">
                    <div className="nx-shelf" style={{ paddingTop: 24, gap: 40 }}>
                        <TitledSet title="This browser" count={here.length} empty="This browser’s session isn’t listed.">
                            {here.length > 0 && sessionTable('security-here', 'This browser', here)}
                        </TitledSet>
                        <TitledSet title="Everywhere else" count={there.length} empty="Nowhere else. This browser is the only place you’re signed in.">
                            {there.length > 0 && sessionTable('security-sessions', 'Everywhere else', there)}
                        </TitledSet>
                    </div>
                </div>
            )}
            {signIns && (
                <FormColumn>
                    <FormBand title="Recent sign-ins" aside="last 30 days" lead="Every sign-in and failed attempt. If you don’t recognise one, end that session and sign out everywhere else.">
                        {signIns.error && <Note tone="warn">Couldn’t read your recent sign-ins.</Note>}
                    </FormBand>
                </FormColumn>
            )}
            {Array.isArray(signIns) && (
                <div className="self-rows">
                    <div className="nx-shelf" style={{ paddingTop: 24 }}>
                        <TitledSet title="Sign-ins" count={signIns.length} empty="No sign-ins in the last 30 days.">
                            {signIns.length > 0 && (
                                <DataTable
                                    id="security-sign-ins" label="Sign-ins" columns={SIGN_IN_COLUMNS} rows={signIns}
                                    keyOf={(a) => `${a.at}-${signIns.indexOf(a)}`} sort={{ id: 'when', dir: 'desc' }}
                                />
                            )}
                        </TitledSet>
                    </div>
                </div>
            )}
            </>)}
        </NucleusPage>
    );
}

function SessionBand({ initial }) {
    const { user } = useAuth();
    const toast = useToast();
    const [saved, setSaved] = useState(initial);
    const [value, setValue] = useState(initial ? String(initial) : '');
    const presetKnown = SESSION_PRESETS.some((p) => p.value === value);
    return (
        <FormBand
            title="Session length"
            aside={`Now ${formatDuration(saved)}`}
            lead="How long you can be away before you sign in again. Using ofSelf restarts the clock, so it never interrupts you mid-work. It applies everywhere you are signed in."
            acts={[{
                id: 'save', label: 'Save', primary: true,
                disabled: String(saved || '') === value,
                run: async () => {
                    const next = value ? Number(value) : null;
                    await api.updateUser(user.id, { session_duration: next });
                    setSaved(next);
                    toast.success(`Signed out after ${next ? formatDuration(next) : '30 days'} away`);
                },
            }]}
        >
            <Segmented
                label="Session length"
                options={presetKnown ? SESSION_PRESETS : [...SESSION_PRESETS, { value, label: formatDuration(Number(value)) }]}
                value={value}
                onChange={setValue}
            />
        </FormBand>
    );
}
